import React, { useState, useEffect } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { useNavigate, Link } from "react-router-dom";
import { 
  Globe, 
  Smartphone, 
  Cpu, 
  FileText, 
  ArrowRight, 
  ChevronRight, 
  Award, 
  ShieldCheck,
  Activity,
  Layers
} from "lucide-react";
import usb from '../images/usb.jpg';
import street from '../images/streetsence.jpg';
import food from '../images/food.jpg';
import skillora from '../images/skillora.jpg';
import phying from '../images/phing.jpg';
import raffle from '../images/raffle.jpg';
import logo from '../images/logoo.png';
import ChatWidget from "./CustomerChat.jsx";
import skillora2 from '../images/skillora2.jpeg';
import skillora3 from '../images/skillora3.jpeg';
import chesshome from '../images/chesshome.png';
import chess2 from '../images/chess2.png';
import Navbar from "../components/Navbar";

const services = [
  {
    title: "Enterprise Web Development",
    icon: <Globe className="w-8 h-8" />,
    path: "/web-development",
    desc: "Scalable web applications built with enterprise-grade security, performance optimization, and modern architectures.",
    technologies: ["React", "Node.js", "Cloud Infrastructure", "API Development"],
  },
  {
    title: "Mobile App Solutions",
    icon: <Smartphone className="w-8 h-8" />,
    path: "/mobile-development",
    desc: "Cross-platform and native mobile applications with seamless user experiences and robust backend integration.",
    technologies: ["Flutter", "React Native", "iOS", "Android"],
  },
  {
    title: "AI & Machine Learning",
    icon: <Cpu className="w-8 h-8" />,
    path: "/ai-ml-solutions",
    desc: "Intelligent automation solutions, predictive analytics, and custom AI models to transform your business operations.",
    technologies: ["Python", "TensorFlow", "Machine Learning", "Data Analytics"],
  },
  {
    title: "Technical Documentation",
    icon: <FileText className="w-8 h-8" />,
    path: "/documentation",
    desc: "Professional documentation services, technical writing, and knowledge management solutions for enterprises.",
    technologies: ["Technical Writing", "API Docs", "User Guides", "Compliance"],
  },
];

const companyStats = [
  { number: "50+", label: "Projects Delivered", icon: <Layers className="w-5 h-5" />, description: "Successfully completed projects across various industries" },
  { number: "15+", label: "Enterprise Clients", icon: <Award className="w-5 h-5" />, description: "Trusted by leading companies and organizations" },
  { number: "99.9%", label: "Uptime Guarantee", icon: <ShieldCheck className="w-5 h-5" />, description: "Reliable systems with exceptional performance" },
  { number: "24/7", label: "Expert Support", icon: <Activity className="w-5 h-5" />, description: "Round-the-clock support and maintenance" }
];

const caseStudies = [
  {
    title: "Skillora - AI Learning Platform",
    date: "2024",
    img: skillora,
    description: "Comprehensive e-learning platform with AI-driven personalization and advanced analytics",
    results: ["500+ Active Users", "40% Improved Outcomes", "98% Satisfaction"],
    technologies: ["React", "Node.js", "AI/ML", "MongoDB"],
    industry: "Education Technology",
    challenge: "Traditional learning methods were ineffective for personalized education",
    solution: "AI-powered adaptive learning system with real-time progress tracking",
    impact: "Revolutionized online education delivery"
  },
  {
    title: "StreetSense - Smart City Platform",
    date: "2024",
    img: street,
    description: "IoT-enabled urban monitoring and management system for smart city infrastructure",
    results: ["30% Crime Reduction", "50% Faster Response", "Real-time Analytics"],
    technologies: ["IoT", "Computer Vision", "React", "Python"],
    industry: "Smart Cities",
    challenge: "Manual monitoring systems were inefficient and reactive",
    solution: "Automated IoT-based monitoring with predictive analytics",
    impact: "Enhanced urban safety and operational efficiency"
  },
  {
    title: "FoodFlowSL - Restaurant Management",
    date: "2024",
    img: food,
    description: "Complete restaurant management and delivery platform with integrated POS system",
    results: ["200+ Restaurants", "25% Revenue Increase", "Reduced Wait Times"],
    technologies: ["React Native", "Node.js", "Real-time APIs", "Payment Integration"],
    industry: "Food & Beverage",
    challenge: "Fragmented restaurant operations and delivery management",
    solution: "Unified platform for orders, inventory, and delivery management",
    impact: "Streamlined restaurant operations and improved customer experience"
  },
  {
    title: "Ceylon Chess - National Platform",
    date: "2024",
    img: chesshome,
    description: "Building a National Tournament Management Platform for the Sri Lankan chess circuit.",
    results: ["High-Performance Delivery", "Responsive Interface", "Dynamic Event Management"],
    technologies: ["React", "Node.js", "Tournament Engine", "Cloud Infrastructure"],
    industry: "Sports & Community",
    challenge: "Handling high-traffic demands and tournament data management for the Sri Lankan chess circuit.",
    solution: "A web-based solution with a dynamic event management system and optimized content delivery.",
    impact: "Centralized tournament management and improved accessibility for the national chess community."
  }
];

export default function Home() {
  const navigate = useNavigate();
  const [selectedCase, setSelectedCase] = useState(null);
  const sliderImages = [chesshome, skillora, street, food, chess2, skillora3, raffle, phying, skillora2];
  const [currentSlide, setCurrentSlide] = useState(0);
  const [mousePos, setMousePos] = useState({ x: 0, y: 0 });

  useEffect(() => {
    const handleMouseMove = (e) => {
      setMousePos({ x: e.clientX, y: e.clientY });
    };
    window.addEventListener("mousemove", handleMouseMove);
    return () => window.removeEventListener("mousemove", handleMouseMove);
  }, []);

  useEffect(() => {
    const interval = setInterval(() => {
      setCurrentSlide((prev) => (prev + 1) % sliderImages.length);
    }, 5000);
    return () => clearInterval(interval);
  }, [sliderImages.length]);

  return (
    <div className="min-h-screen bg-[#020617] text-slate-100 selection:bg-blue-500/20 transition-colors duration-500 font-inter scroll-smooth overflow-x-hidden">
      <div className="bg-noise" />
      <motion.div 
        className="cursor-light hidden lg:block"
        animate={{ 
          x: mousePos.x - 300, 
          y: mousePos.y - 300 
        }}
        transition={{ type: "spring", damping: 30, stiffness: 100 }}
      />
      
      <Navbar />
      <ChatWidget />

      {/* Hero Section */}
      <section className="relative pt-48 pb-32 px-6 overflow-hidden">
        {/* Background Patterns & Blurs */}
        <div className="absolute inset-0 bg-grid opacity-10 pointer-events-none" />
        <div className="absolute top-0 right-0 w-[800px] h-[800px] bg-blue-500/10 blur-[150px] rounded-full -mr-96 -mt-96 animate-blob" />
        <div className="absolute bottom-0 left-0 w-[600px] h-[600px] bg-indigo-500/10 blur-[150px] rounded-full -ml-48 -mb-48 animate-blob delay-2000" />
        <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-full h-full bg-mesh opacity-30 pointer-events-none" />

        <div className="container mx-auto max-w-7xl relative z-10">
          <div className="grid lg:grid-cols-2 gap-16 lg:gap-32 items-center">
            <motion.div
              initial={{ opacity: 0, x: -30 }}
              animate={{ opacity: 1, x: 0 }}
              transition={{ duration: 0.8, ease: "easeOut" }}
            >
              <div className="inline-flex items-center gap-3 px-4 py-1.5 bg-slate-900/40/5 backdrop-blur-md border border-white/10 rounded-full mb-8 shadow-2xl">
                <span className="relative flex h-2 w-2">
                  <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-blue-500 opacity-75"></span>
                  <span className="relative inline-flex rounded-full h-2 w-2 bg-blue-600"></span>
                </span>
                <span className="text-[10px] font-bold text-slate-400 uppercase tracking-widest">Trusted Enterprise Partner</span>
              </div>

              <h1 className="text-6xl md:text-7xl lg:text-8xl font-extrabold text-white mb-8 tracking-tight leading-[1.05]">
                Engineering <br />
                <span className="text-gradient">Tomorrow.</span>
              </h1>

              <p className="text-lg md:text-xl text-slate-400 mb-12 leading-relaxed max-w-xl font-normal">
                We bridge the gap between complex human ambition and high-fidelity computing. Delivering surgical software solutions for the modern enterprise.
              </p>

              <div className="flex flex-wrap gap-6 items-center">
                <motion.button
                  whileHover={{ scale: 1.05 }}
                  whileTap={{ scale: 0.95 }}
                  onClick={() => navigate("/contact")}
                  className="group relative px-10 py-5 bg-blue-600 text-white font-bold rounded-2xl overflow-hidden shadow-2xl shadow-blue-500/20 transition-all active:scale-95"
                >
                  <div className="absolute inset-0 bg-gradient-to-r from-blue-400 to-indigo-600 opacity-0 group-hover:opacity-100 transition-opacity" />
                  <span className="relative z-10 flex items-center gap-3 uppercase tracking-widest text-[11px]">
                    Initiate Sprint
                    <ArrowRight className="w-4 h-4 transition-transform group-hover:translate-x-1" />
                  </span>
                </motion.button>
                <div
                  onClick={() => document.getElementById('services').scrollIntoView({ behavior: 'smooth' })}
                  className="cursor-pointer text-xs font-bold text-slate-400 uppercase tracking-widest hover:text-white transition-colors py-2 border-b-2 border-transparent hover:border-slate-900"
                >
                  Explore Capability
                </div>
              </div>
            </motion.div>

            <motion.div
              initial={{ opacity: 0, scale: 0.95 }}
              animate={{ opacity: 1, scale: 1 }}
              transition={{ duration: 1, delay: 0.2 }}
              className="relative"
            >
              <div className="absolute -inset-4 bg-slate-900/40/20 blur-xl rounded-[4rem]" />
              <div className="glass-card rounded-[4rem] p-4 relative z-10 overflow-hidden border border-white/80 shadow-[0_40px_80px_rgba(0,0,0,0.06)] animate-float">
                <AnimatePresence mode="wait">
                  <motion.div
                    key={currentSlide}
                    className="relative w-full h-[600px] overflow-hidden rounded-[3.5rem]"
                    initial={{ opacity: 0 }}
                    animate={{ opacity: 1 }}
                    exit={{ opacity: 0 }}
                    transition={{ duration: 0.8 }}
                  >
                    <img
                      src={sliderImages[currentSlide]}
                      alt="Operation"
                      className="w-full h-full object-contain bg-[#020617]/50"
                    />
                    <div className="absolute inset-0 bg-gradient-to-t from-slate-900/40 to-transparent" />
                    <div className="absolute bottom-12 left-12 right-12 flex justify-between items-end">
                      <div className="space-y-1">
                        <p className="text-[10px] font-black text-white/60 uppercase tracking-widest">Case Analysis</p>
                        <h4 className="text-2xl font-black text-white uppercase tracking-tighter">Production Node {currentSlide + 1}</h4>
                      </div>
                      <div className="flex gap-2">
                        {sliderImages.map((_, i) => (
                          <div key={i} className={`h-1.5 rounded-full transition-all duration-500 ${currentSlide === i ? 'w-8 bg-blue-500' : 'w-2 bg-slate-900/40/30'}`} />
                        ))}
                      </div>
                    </div>
                  </motion.div>
                </AnimatePresence>
              </div>
            </motion.div>
          </div>
        </div>
      </section>

       {/* Services Section */}
      <section id="services" className="py-40 px-6 relative border-y border-white/5 bg-[#020617] overflow-hidden">
        <div className="absolute inset-0 bg-grid opacity-5 pointer-events-none" />
        <div className="container mx-auto max-w-7xl relative z-10">
          <div className="flex flex-col lg:flex-row justify-between items-start lg:items-end mb-32 gap-16">
            <div className="max-w-3xl">
              <h2 className="text-5xl md:text-6xl font-extrabold text-white mb-6 tracking-tight uppercase leading-none">Our Core <br />Expertise</h2>
              <p className="text-lg md:text-xl text-slate-400 font-normal max-w-xl">High-fidelity deployment logic for cross-sector operations and enterprise-scale solutions.</p>
            </div>
            <div className="flex flex-col lg:flex-row gap-12 items-center px-10 py-10 bg-white/5 backdrop-blur-2xl rounded-[3rem] border border-white/10 shadow-2xl">
              {companyStats.map((stat, i) => (
                <div key={i} className="flex flex-col items-center lg:items-start text-center lg:text-left">
                  <div className="flex items-center gap-3 mb-2">
                    <span className="text-blue-500 bg-blue-500/10 p-2 rounded-xl border border-blue-500/20">{stat.icon}</span>
                    <span className="text-5xl font-extrabold text-white tracking-tighter">{stat.number}</span>
                  </div>
                  <div className="text-[11px] uppercase tracking-[0.3em] text-slate-500 font-black">{stat.label}</div>
                </div>
              ))}
            </div>
          </div>

          <div className="grid md:grid-cols-2 gap-12">
            {services.map((service, idx) => (
              <motion.div
                key={idx}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                onClick={() => navigate(service.path)}
                transition={{ duration: 0.5, delay: idx * 0.1 }}
                className="group premium-card p-10 md:p-12 rounded-[2.5rem] cursor-pointer"
              >
                <div className="w-16 h-16 bg-slate-900/40/5 text-blue-500 mb-10 flex items-center justify-center rounded-2xl border border-white/10 transition-all duration-500 group-hover:bg-blue-600 group-hover:text-white group-hover:rotate-3 group-hover:scale-105">
                  {service.icon}
                </div>
                <h3 className="text-2xl font-extrabold text-white mb-4 tracking-tight uppercase leading-none">{service.title}</h3>
                <p className="text-slate-400 mb-8 text-base leading-relaxed font-normal">{service.desc}</p>
                <div className="flex flex-wrap gap-2 mb-10">
                  {service.technologies.map((tech, i) => (
                    <span key={i} className="px-4 py-1.5 bg-slate-900/40/5 text-[9px] font-bold text-slate-400 uppercase tracking-widest border border-white/10 rounded-full group-hover:text-blue-400 group-hover:border-blue-500/20 transition-colors">
                      {tech}
                    </span>
                  ))}
                </div>
                <div className="flex items-center justify-end">
                  <div className="flex items-center gap-2 text-blue-600 font-bold transition-all group-hover:gap-4 uppercase tracking-widest text-[10px]">
                    Explore Solutions
                    <ChevronRight className="w-4 h-4" />
                  </div>
                </div>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Case Studies Section */}
      <section className="py-40 px-6 relative overflow-hidden bg-slate-900/40">
        <div className="absolute top-0 right-0 w-[600px] h-[600px] bg-slate-100 blur-[120px] rounded-full -mr-48 -mt-48 opacity-60" />
        <div className="container mx-auto max-w-7xl relative z-10">
          <div className="text-center mb-24">
            <h2 className="text-5xl md:text-6xl font-extrabold text-white mb-6 tracking-tight uppercase leading-none">Featured <br /><span className="text-gradient">Case Studies.</span></h2>
            <div className="h-1 w-20 bg-blue-600 mx-auto rounded-full"></div>
          </div>

          <div className="flex flex-wrap justify-center gap-12">
            {caseStudies.map((study, idx) => (
              <motion.div
                key={idx}
                initial={{ opacity: 0, y: 30 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.6, delay: idx * 0.1 }}
                className="group relative bg-slate-900/40 rounded-3xl overflow-hidden border border-white/5 hover:shadow-xl transition-all duration-500 w-full md:w-[calc(50%-1.5rem)] lg:w-[calc(33.333%-2rem)]"
              >
                <div className="relative h-64 overflow-hidden">
                  <img
                    src={study.img}
                    alt={study.title}
                    className="w-full h-full object-contain bg-[#020617] group-hover:scale-105 transition-all duration-700"
                  />
                  <div className="absolute inset-0 bg-gradient-to-t from-white via-transparent to-transparent opacity-60" />
                  <span className="absolute top-6 left-6 px-4 py-2 bg-slate-900/40/90 backdrop-blur-md rounded-lg text-[9px] font-bold text-white uppercase tracking-widest border border-white shadow-sm">
                    {study.industry}
                  </span>
                </div>

                 <div className="p-8 pt-4">
                  <h3 className="text-xl font-extrabold text-white mb-6 tracking-tight uppercase leading-tight group-hover:text-blue-400 transition-colors">{study.title}</h3>
                  <div className="space-y-3 mb-8">
                    {study.results.map((r, i) => (
                      <div key={i} className="flex items-center gap-3 text-[10px] font-bold text-blue-500 uppercase tracking-widest">
                        <div className="w-1.5 h-1.5 bg-blue-600 rounded-full" />
                        {r}
                      </div>
                    ))}
                  </div>

                  <button
                    onClick={() => setSelectedCase(selectedCase === idx ? null : idx)}
                    className="w-full py-4 bg-slate-900/40/5 hover:bg-blue-600 text-white text-[9px] font-bold uppercase tracking-widest rounded-xl transition-all border border-white/10 shadow-2xl"
                  >
                    {selectedCase === idx ? "Close Report" : "View Details"}
                  </button>

                  <AnimatePresence>
                    {selectedCase === idx && (
                       <motion.div
                        initial={{ opacity: 0, height: 0 }}
                        animate={{ opacity: 1, height: "auto" }}
                        exit={{ opacity: 0, height: 0 }}
                        className="overflow-hidden mt-6 pt-6 border-t border-white/5 space-y-6"
                      >
                        <div className="space-y-2">
                          <label className="text-[9px] uppercase font-bold text-slate-400 tracking-widest block">The Challenge</label>
                          <p className="text-sm text-slate-400 leading-relaxed font-normal">{study.challenge}</p>
                        </div>
                        <div className="space-y-2 pb-2">
                          <label className="text-[9px] uppercase font-bold text-slate-400 tracking-widest block">Our Solution</label>
                          <p className="text-sm text-slate-400 leading-relaxed font-normal">{study.solution}</p>
                        </div>
                      </motion.div>
                    )}
                  </AnimatePresence>
                </div>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-48 px-6 relative z-10 border-t border-white/5 bg-[#f8fafc]/50">
        <div className="absolute inset-0 bg-grid opacity-5" />
        <div className="container mx-auto max-w-5xl relative z-10">
          <motion.div
            initial={{ opacity: 0, scale: 0.98 }}
            whileInView={{ opacity: 1, scale: 1 }}
            viewport={{ once: true }}
            className="bg-slate-900 p-16 md:p-24 rounded-[3rem] text-center relative overflow-hidden shadow-2xl"
          >
            <div className="absolute top-0 right-0 w-96 h-96 bg-blue-600/20 blur-[120px] rounded-full -mr-32 -mt-32" />
            <div className="absolute bottom-0 left-0 w-96 h-96 bg-indigo-600/10 blur-[120px] rounded-full -ml-32 -mb-32" />
            
            <h2 className="text-5xl md:text-7xl font-extrabold text-white mb-8 tracking-tight leading-none relative z-10 uppercase">Scale Your <br /><span className="text-blue-400">Future.</span></h2>
            <p className="text-lg md:text-xl text-slate-400 mb-12 max-w-2xl mx-auto font-normal relative z-10">
              Expert engineering slots available for Q3. Partner with LAB Developers to build secure, scalable, and sophisticated architectures.
            </p>
            <div className="flex flex-col sm:flex-row justify-center gap-6 relative z-10">
              <button
                onClick={() => navigate("/contact")}
                className="px-10 py-5 bg-blue-600 text-white font-bold rounded-2xl hover:bg-blue-500 transition-all shadow-lg hover:-translate-y-1 uppercase tracking-widest text-xs"
              >
                Request Consultation
              </button>
              <a
                href="https://wa.me/94723954658"
                className="px-10 py-5 bg-slate-900/40/10 backdrop-blur-md border border-white/10 text-white font-bold rounded-2xl hover:bg-slate-900/40/20 transition-all flex items-center justify-center gap-3 uppercase tracking-widest text-xs"
              >
                Contact via WhatsApp
              </a>
            </div>
          </motion.div>
        </div>
      </section>

      <footer className="py-32 px-6 border-t border-white/5 bg-slate-900/40 relative z-20">
        <div className="container mx-auto max-w-7xl">
          <div className="flex flex-col md:flex-row justify-between items-center gap-12 mb-20">
            <Link to="/" className="flex items-center gap-4 group">
              <img src={logo} alt="Logo" className="w-12 h-12" />
              <span className="text-2xl font-extrabold text-white tracking-tight uppercase leading-none">LAB <br /><span className="text-blue-600 text-xs tracking-widest block mt-1">DEVELOPERS</span></span>
            </Link>
            <div className="flex flex-wrap justify-center gap-10 text-[10px] uppercase font-bold text-slate-400 tracking-widest">
              <span className="hover:text-blue-600 cursor-pointer transition-colors">Solutions</span>
              <span className="hover:text-blue-600 cursor-pointer transition-colors">Security</span>
              <span className="hover:text-blue-600 cursor-pointer transition-colors">About</span>
              <span className="hover:text-blue-600 cursor-pointer transition-colors">Careers</span>
            </div>
          </div>
          <div className="pt-12 border-t border-slate-50 flex flex-col md:flex-row justify-between items-center gap-6">
            <p className="text-[10px] font-bold text-slate-400 uppercase tracking-widest">© 2024 LAB Developers. All rights reserved.</p>
            <div className="flex items-center gap-4">
              <div className="w-2 h-2 bg-green-500 rounded-full animate-pulse" />
              <span className="text-[10px] font-bold text-slate-400 uppercase tracking-widest">Network Status: Operational</span>
            </div>
          </div>
        </div>
      </footer>
    </div>
  );
}
